type VersionManifest = {
  version?: string;
};

type CheckForAppUpdateOptions = {
  currentVersion?: string;
  fetcher?: typeof fetch;
  storage?: Storage;
  now?: () => number;
  minIntervalMs?: number;
};

const appUpdateReloadKey = 'lfc_app_update_reload_target';
const appUpdateLastCheckKey = 'lfc_app_update_last_check_at';
const defaultMinIntervalMs = 30 * 1000;

const getVersionUrl = (): string => {
  const baseUrl = import.meta.env.BASE_URL || '/';
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  return `${normalizedBaseUrl}version.json?t=${Date.now()}`;
};

const getReloadTarget = (storage: Storage): string | null => {
  try {
    return storage.getItem(appUpdateReloadKey);
  } catch {
    return null;
  }
};

const setReloadTarget = (storage: Storage, version: string): void => {
  try {
    storage.setItem(appUpdateReloadKey, version);
  } catch {
    // Failing to persist the loop guard should not block the update reload.
  }
};

const clearReloadTarget = (storage: Storage): void => {
  try {
    storage.removeItem(appUpdateReloadKey);
  } catch {
    // Ignore storage failures. They only affect loop guarding.
  }
};

const getLastCheckAt = (storage: Storage): number | null => {
  try {
    const lastCheckAtValue = storage.getItem(appUpdateLastCheckKey);
    if (lastCheckAtValue !== null) {
      const lastCheckAt = Number(lastCheckAtValue);
      return Number.isFinite(lastCheckAt) ? lastCheckAt : null;
    }
  } catch {
    return null;
  }

  return null;
};

const setLastCheckAt = (storage: Storage, now: number): void => {
  try {
    storage.setItem(appUpdateLastCheckKey, String(now));
  } catch {
    // Ignore storage failures. They only affect throttling.
  }
};

const shouldThrottleCheck = (storage: Storage, now: number, minIntervalMs: number): boolean => {
  const lastCheckAt = getLastCheckAt(storage);
  return lastCheckAt !== null && now - lastCheckAt < minIntervalMs;
};

export const markAppUpdateReloadTarget = (
  version: string,
  storage: Storage = window.sessionStorage
): void => {
  setReloadTarget(storage, version);
};

export const checkForAppUpdate = async ({
  currentVersion = __APP_VERSION__,
  fetcher = window.fetch.bind(window),
  storage = window.sessionStorage,
  now = () => Date.now(),
  minIntervalMs = defaultMinIntervalMs
}: CheckForAppUpdateOptions = {}): Promise<string | null> => {
  try {
    const checkStartedAt = now();
    if (shouldThrottleCheck(storage, checkStartedAt, minIntervalMs)) {
      return null;
    }

    const response = await fetcher(getVersionUrl(), {cache: 'no-store'});
    if (!response.ok) {
      return null;
    }
    setLastCheckAt(storage, checkStartedAt);

    const manifest = (await response.json()) as VersionManifest;
    const latestVersion = manifest.version;
    if (!latestVersion || latestVersion === currentVersion) {
      clearReloadTarget(storage);
      return null;
    }

    if (getReloadTarget(storage) === latestVersion) {
      return null;
    }

    return latestVersion;
  } catch (error) {
    console.warn('Failed to check app version:', error);
    return null;
  }
};
